import { formatDate, buildLocationLabel, participationCountsText, sightDivisionLabel, numberOrZero, normalizeBullseyeClassificationLabel, isBullseyeTwentyTwoOnlyRow, buildBullseyeAwardBlocks, buildBullseyePostedSections, buildBullseyeBulletinDocument, bullseyeRowKey } from "./bullseyeBulletinModel";
import "../Matches/PortalSurfaces.css";
import { displayReturn } from "../Matches/matchNavigation";
import CancelledMatchDetail from "../Matches/CancelledMatchDetail";
import RelatedMatchSelector from "../Matches/RelatedMatchSelector";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getCurrentUser } from "aws-amplify/auth";
import MatchLocationMap from "../Matches/MatchLocationMap";
import UpcomingMatchDetail from "../Matches/UpcomingMatchDetail";
import { isUpcomingDetail } from "../Matches/upcomingMatch";
import useMatchPageRefresh from "../Matches/useMatchPageRefresh";
import InProgressMatchDetail from "../Matches/InProgressMatchDetail";
import CompletedMatchDetail from "../Matches/CompletedMatchDetail";
import PublicMatchDownloads from "../Matches/PublicMatchDownloads";
import MatchShareMenu from "../Matches/MatchShareMenu";
import { completedBullseyeSections, isCompletedDetail } from "../Matches/completedResults";
import { bullseyeLiveSections, isInProgressDetail } from "../Matches/liveMatch";
import { getMatchCoordinates } from "../Matches/matchCoordinates";
import DetailOverlay from "./DetailOverlay";
import { BoothDisplayStatus, BoothLeaderboardDisplay } from "./LeaderboardMatchDetail";
import {
  getBullseyeMatch,
  getMyBullseyeMatchRegistration,
  getBullseyeMatchRegistrationSummary,
  getBullseyeMatchSetupDetail,
  listBullseyeMatchLeaderboards,
  listBullseyeMatchLeaderboardsPage,
  listBullseyeTeamLeaderboards,
  listCompetitionResults,
} from "../../lib/leaderboardApi";
import { CompetitionErrorState } from "../Competition/CompetitionRoutePanels";
import { rankBullseyeRows, rankBullseyeParticipationRows, groupBullseyeDisplayRows, hasBullseyeScoreData } from "./bullseyePostedRanking";
import { bullseyeTieContext } from "./bullseyeTieBreaks";
import useBullseyeDisplayRotation from "./useBullseyeDisplayRotation";
import { BullseyeBulletinPrintControls, BullseyeBulletinPrintHeader } from "./BullseyeBulletinPrint";
import BullseyeAwardsCsvExport from "./BullseyeAwardsCsvExport";
import { normalizeResultClass } from "./bullseyeResultGrouping";
import "./Leaderboard.css";

export { buildBullseyePostedSections, buildBullseyeAwardBlocks, buildBullseyeBulletinDocument } from "./bullseyeBulletinModel";

function formatStatus(value) {
  if (value === "official") return "Recorded scores confirmed";
  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/^./, (char) => char.toUpperCase());
}

function isResultsPhaseStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return ["in_progress", "completed", "final", "official", "closed"].includes(normalized);
}

function buildAddressLabel(match) {
  const rangeName = String(match?.rangeName || "").trim();
  const rangeAddress = String(match?.rangeAddress || "").trim();
  const city = String(match?.city || "").trim();
  const state = String(match?.state || "").trim();

  const parts = [];
  if (rangeName) parts.push(rangeName);
  if (rangeAddress) parts.push(rangeAddress);
  const addressLower = rangeAddress.toLowerCase();
  const alreadyHasCity = city && addressLower.includes(city.toLowerCase());
  const alreadyHasState = state && addressLower.includes(state.toLowerCase());
  if (!rangeAddress && city && state) {
    parts.push(`${city}, ${state}`);
  } else if (!rangeAddress && city) {
    parts.push(city);
  } else if (!rangeAddress && state) {
    parts.push(state);
  } else if (rangeAddress && city && state && !alreadyHasCity && !alreadyHasState) {
    parts.push(`${city}, ${state}`);
  }

  return parts.filter(Boolean).join(" • ") || "Address not set";
}

function hasMapLocation(match) {
  return Boolean(getMatchCoordinates(match));
}

function summaryCounts(rows) {
  const initial = {
    total: rows.length,
    unscored: 0,
    in_progress: 0,
    scored: 0,
    audited: 0,
    official: 0,
    needs_refire: 0,
    entered: 0,
  };

  return rows.reduce((running, row) => {
    const status = String(row?.status || "").trim().toLowerCase();
    if (Object.prototype.hasOwnProperty.call(running, status)) {
      running[status] += 1;
    }
    const hasScoreData = Number(row?.shotsFired || 0) > 0 || Number(row?.totalScore || 0) > 0 || Number(row?.xCount || 0) > 0;
    if (hasScoreData || ["scored", "audited", "official", "needs_refire"].includes(status)) {
      running.entered += 1;
    }
    return running;
  }, initial);
}

function scoreText(score, xCount) {
  return Number(xCount || 0) > 0 ? `${Number(score || 0)}-${Number(xCount || 0)}X` : `${Number(score || 0)}`;
}

function shotsText(shotsFired) {
  if (shotsFired === undefined || shotsFired === null) return "Shots not reported";
  return `Shots fired ${Number(shotsFired) || 0}`;
}

function formatLastUpdated(value) {
  if (!value) return "";
  return value.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function bullseyeDisplayPath(matchId, rows) {
  const path = `/matches/bullseye/${encodeURIComponent(matchId || "")}/display`;
  return rows === "all" ? `${path}?rows=all` : path;
}

const BULLSEYE_LEADERBOARD_BANNER_SRC = "/Leaderboard_Bullseye.svg";
const BULLSEYE_DETAIL_INITIAL_ROW_LIMIT = 50;
const BULLSEYE_DISPLAY_DEFAULT_ROW_LIMIT = "all";
const BULLSEYE_DISPLAY_REFRESH_MS = 15000;
const BULLSEYE_DISPLAY_SECTION_ROTATE_MS = 18000;
const BULLSEYE_AWARD_VIEW_OPTIONS = [
  { value: "bulletin", label: "Bulletin" },
  { value: "overall", label: "Overall" },
  { value: "class", label: "Class" },
  { value: "category", label: "Category" },
  { value: "twenty_two", label: "22 Only" },
];

function normalizeDisplayRowCount(value, fallback = 20) {
  if (String(value || "").trim().toLowerCase() === "all") {
    return "all";
  }
  const numericValue = Number.parseInt(String(value || ""), 10);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : fallback;
}

function getDisplayRowsParam(searchParams) {
  return searchParams.get("rows") || searchParams.get("top");
}

function bullseyeEnteredScoreText(value) {
  return hasBullseyeScoreData(value) ? scoreText(value.totalScore, value.xCount) : "-";
}

function BullseyeClassificationLabel({ value }) {
  if (normalizeBullseyeClassificationLabel(value) === "Unclassified") {
    return <abbr className="bullseye-unclassified-label" title="Unclassified" aria-label="Unclassified">UNC</abbr>;
  }
  return String(value || "").trim();
}

function sectionAnchorId(key) {
  return `bullseye-section-${String(key || "section").replace(/[^a-zA-Z0-9]+/g, "-")}`;
}

function bullseyeCompactFirearmName(value) {
  return String(value || "")
    .replace(/\s*\([^)]*\)/g, "")
    .replace(/\s+Aggregate$/i, "")
    .trim();
}

function bullseyeFirearmQualifier(value) {
  return String(value || "").match(/\(([^)]+)\)/)?.[1]?.trim() || "";
}

function bullseyeSectionNavLabel(section) {
  const title = String(section?.title || "").trim();
  if (!title) return "Section";
  if (section?.kind === "firearm") {
    return `${bullseyeCompactFirearmName(title)} Total`;
  }
  return title;
}

function bullseyeSectionNavMeta(section) {
  if (section?.kind === "match") return "Overall";
  if (section?.kind === "course") return "Fired match";
  if (section?.kind === "firearm") return bullseyeFirearmQualifier(section.title) || "Firearm total";
  return "Results";
}

function BullseyePostedResultTable({ rows = [], ariaLabel, bulletin = false, placeLabel = "Place" }) {
  return (
    <div className={`bullseye-posted-table${bulletin ? " bullseye-posted-table-bulletin" : ""}`} role="table" aria-label={ariaLabel}>
      <div className="bullseye-posted-table-row bullseye-posted-table-head" role="row">
        <span>{placeLabel}</span>
        <span>Competitor</span>
        <span>Class</span>
        <span>Score</span>
        <span>X</span>
        {bulletin && <span>Award</span>}
      </div>
      {rows.map((row, index) => (
        <div key={`${row.rowKey}-${row.awardRank || row.rank}-${row.awardText || "standing"}-${index}`} className="bullseye-posted-table-row" role="row">
          <span className="bullseye-posted-cell-place" data-label={placeLabel} title={row.tiePendingReason || (row.tieBreakRule ? `${row.tieBreakLabel} (NRA ${row.tieBreakRule})` : undefined)} aria-label={row.isTied ? `Tied ${row.rank}` : undefined}>{row.isTied ? `T${row.rank}` : row.awardRank || `#${row.rank}`}</span>
          <div className="bullseye-posted-cell-competitor" data-label="Competitor">
            <strong>{row.competitorName}</strong>
            {sightDivisionLabel(row.division) && <span className="bullseye-sight-division">{sightDivisionLabel(row.division)}</span>}
            {row.tieBreakRule && <span className="bullseye-class-award-note" title={row.tieBreakLabel}>NRA {row.tieBreakRule}</span>}
            {!bulletin && row.awardNote && <span className="bullseye-class-award-note">{row.awardNote}</span>}
          </div>
          <span className="bullseye-posted-cell-class" data-label="Class"><BullseyeClassificationLabel value={row.classification} /></span>
          <span className="bullseye-posted-cell-score" data-label="Score">{row.totalScore}</span>
          <span className="bullseye-posted-cell-x" data-label="X">{row.xCount}X</span>
          {bulletin && <span className="bullseye-posted-cell-award" data-label="Award">{row.awardText || ""}{row.awardNote && <span className="bullseye-class-award-note">{row.awardNote}</span>}</span>}
        </div>
      ))}
    </div>
  );
}

export function BullseyePostedAwardBlock({ block, sectionTitle, bulletin = false }) {
  if (Array.isArray(block.children) && block.children.length > 0) {
    return (
      <div className="bullseye-award-block bullseye-award-block-group">
        <h4>{block.title}</h4>
        <div className="bullseye-award-subgroup-stack">
          {block.children.map((child) => (
            <div key={child.key} className="bullseye-award-subgroup">
              <h5>{child.title}</h5>
              {child.awardSummary && <p className="bullseye-class-award-summary">{child.awardSummary}</p>}
              <BullseyePostedResultTable rows={child.rows} ariaLabel={`${sectionTitle} ${block.title}: ${child.title}`} bulletin={bulletin} placeLabel={child.placeLabel} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!Array.isArray(block.rows) || block.rows.length === 0) {
    return (
      <div className="bullseye-award-block bullseye-award-empty">
        {block.title && <h4>{block.title}</h4>}
        <strong>{block.emptyTitle || "No standings in this view"}</strong>
      </div>
    );
  }

  return (
    <div className="bullseye-award-block">
      {block.title && <h4>{block.title}</h4>}
      {block.awardSummary && <p className="bullseye-class-award-summary">{block.awardSummary}</p>}
      <BullseyePostedResultTable rows={block.rows} ariaLabel={`${sectionTitle} ${block.title || "overall standings"}`} bulletin={bulletin} placeLabel={block.placeLabel} />
    </div>
  );
}

function getBullseyePostedSectionRows(rows = [], sectionKey = "", settings) {
  const normalizedSectionKey = String(sectionKey || "").trim();
  const section = normalizedSectionKey
    ? buildBullseyePostedSections(rows, settings).find((entry) => entry.key === normalizedSectionKey) : null;
  const sourceRows = section?.rows || buildBullseyePostedSections(rows, settings).find(entry => entry.kind === "match")?.entrantRows || normalizeBullseyeDisplayRows(rows, { compact: true });
  return groupBullseyeDisplayRows(sourceRows.map((row) => ({
    ...row,
    twentyTwoOnly: isBullseyeTwentyTwoOnlyRow(row),
    matchId: "",
    sk: row.rowKey || row.sk,
    competitorId: row.rowKey || row.competitorId,
    competitorName: row.competitorName,
    identityMetaItems: [
      normalizeResultClass(row.classification).replace(/^Unclassified$/, "UNC"),
      sightDivisionLabel(row.division).replace(" division", ""),
    ].filter(Boolean),
    totalScore: row.totalScore,
    xCount: row.xCount,
    shotsFired: row.shotsFired,
    plannedShots: row.plannedShots,
    status: row.status,
    rank: row.rank,
    isTied: row.isTied,
    rankDisplay: row.isTied ? `T${row.rank}` : undefined,
    teamName: hasBullseyeScoreData(row) ? `${numberOrZero(row.xCount)}X` : "-",
    scoreDisplay: hasBullseyeScoreData(row) ? String(numberOrZero(row.totalScore)) : "-",
    performanceDisplay: hasBullseyeScoreData(row) ? shotsText(row.shotsFired) : "Not scored",
  })));
}

function bullseyeSectionDisplayPath(matchId, sectionKey = "") {
  const path = bullseyeDisplayPath(matchId, "all");
  return sectionKey ? `${path}&section=${encodeURIComponent(sectionKey)}` : path;
}

function normalizeBullseyeDisplayRows(rows = [], { compact = false } = {}) {
  return rankBullseyeRows((Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    totalScore: numberOrZero(row.totalScore),
    xCount: numberOrZero(row.xCount),
  })))
    .map((row) => {
      const displayFields = {
        isTied: row.isTied,
        rankDisplay: row.isTied ? `T${row.rank}` : undefined,
        teamName: `${numberOrZero(row?.xCount)}X`,
        scoreDisplay: String(numberOrZero(row?.totalScore)),
        performanceDisplay: shotsText(row?.shotsFired),
      };

      return compact
        ? {
          matchId: row?.matchId,
          sk: row?.sk,
          competitorId: row?.competitorId,
          competitorName: row?.competitorName,
          classification: row?.classification,
          division: row?.division,
          categories: row?.categories,
          category: row?.category,
          totalScore: row?.totalScore,
          xCount: row?.xCount,
          shotsFired: row?.shotsFired,
          plannedShots: row?.plannedShots,
          totalScoreUnits: row?.totalScoreUnits,
          completedScoreUnits: row?.completedScoreUnits,
          officialScoreUnits: row?.officialScoreUnits,
          status: row?.status,
          rank: row?.rank,
          ...displayFields,
        }
        : {
          ...row,
          ...displayFields,
        };
    });
}

function normalizeCanonicalBullseyeResultRows(rows = []) {
  const mappedRows = (Array.isArray(rows) ? rows : [])
    .filter((row) => String(row?.discipline || "").toLowerCase() === "bullseye")
    .map((row) => ({
      ...row,
      sk: row.resultId,
      competitorId: row.participantId,
      competitorName: row.displayName || "Competitor",
      totalScore: Number(row.classificationScore ?? row.score?.aggregate ?? 0),
      xCount: Number(row.xCount || 0),
      shotsFired: Number(row.shotsFired || 0),
      plannedShots: Number(row.plannedShots || 0),
      totalScoreUnits: Number(row.plannedShots || 0),
      completedScoreUnits: Number(row.shotsFired || 0),
      officialScoreUnits: String(row.status || "").toLowerCase() === "official" ? Number(row.shotsFired || 0) : 0,
      status: row.status || "official",
      rank: 0,
      firearms: [],
    }));

  return normalizeBullseyeDisplayRows(mappedRows);
}

function mergeBullseyeRows(existingRows = [], nextRows = []) {
  const merged = new Map();
  [...(Array.isArray(existingRows) ? existingRows : []), ...(Array.isArray(nextRows) ? nextRows : [])].forEach((row, index) => {
    const key = row?.sk || row?.entryId || row?.competitorId || row?.competitorExternalId || `row-${index}`;
    merged.set(String(key), row);
  });
  return normalizeBullseyeDisplayRows([...merged.values()]);
}

async function loadRemainingBullseyeLeaderboardPages(matchId, firstNextToken, onRows, { pageLimit = 100, maxPages = 50 } = {}) {
  let nextToken = firstNextToken || null;
  let pageCount = 0;

  while (nextToken && pageCount < maxPages) {
    const page = await listBullseyeMatchLeaderboardsPage({
      matchId,
      limit: pageLimit,
      nextToken,
    });
    onRows(page.data || []);
    nextToken = page.nextToken || null;
    pageCount += 1;
  }
  if (nextToken) throw new Error("Not all result pages could be loaded.");
}

function formatSetupLabel(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/^./, (char) => char.toUpperCase());
}

const BULLSEYE_FIRE_TYPE_LABELS = { 1: "Slow Fire", 2: "Timed Fire", 3: "Rapid Fire", 99: "Other" };

function setupFacts(setupDetail) {
  const gunGroups = Array.isArray(setupDetail?.gunGroups) ? setupDetail.gunGroups : [];
  const teamConfigurations = Array.isArray(setupDetail?.teamConfigurations) ? setupDetail.teamConfigurations : [];
  const teamEntries = Array.isArray(setupDetail?.teamEntries) ? setupDetail.teamEntries : [];
  const totalStages = gunGroups.reduce((sum, group) => sum + (Array.isArray(group?.stages) ? group.stages.length : 0), 0);

  return {
    firearmCount: gunGroups.length,
    totalStages,
    teamConfigurationCount: teamConfigurations.length,
    teamCount: teamEntries.length,
  };
}

function bullseyeMatchShapeLabel(match, facts) {
  const templateName = String(match?.selectedCourseTemplateName || match?.selectedCourseTemplateCode || "").trim();
  if (/1800/i.test(templateName)) return "NRA Approved 1800 Aggregate";
  if (/2700/i.test(templateName)) return "NRA Approved 2700 Aggregate";
  if (/\b900\b/i.test(templateName)) return "NRA Approved 900 Aggregate";
  if (/national match|nmc/i.test(templateName)) return "National Match Course";

  const firearmCount = Number(facts?.firearmCount || 0);
  if (firearmCount === 2) return "Two Firearm Groups";
  if (firearmCount === 3) return "Three Firearm Groups";
  if (firearmCount === 1) return "Single Firearm Aggregate";
  return templateName || "Bullseye Match";
}

function bullseyeParticipationLabel(match) {
  const raw = String(match?.selectedPistolDiscipline || "").trim();
  const lower = raw.toLowerCase();
  if (!raw) return "";
  if (lower === "mixed") return "Standard + .22 Only";
  if (lower.includes("twenty") || lower.includes("22")) return ".22 Only";
  return formatSetupLabel(raw);
}

function buildBullseyeRegistrationFacts(summary) {
  return {
    registered: Number(summary?.total || 0),
    confirmed: Number(summary?.confirmed || 0),
    pending: Number(summary?.pending || 0),
    registrationOpen: summary?.registrationOpen === true,
  };
}

function BullseyeLeaderboardMatchDetail() {
  const { matchId } = useParams();
  const location = useLocation();
  const [match, setMatch] = useState(null);
  const [setupDetail, setSetupDetail] = useState(null);
  const [registrationSummary, setRegistrationSummary] = useState(null);
  const [registrationSummaryError, setRegistrationSummaryError] = useState("");
  const [setupError, setSetupError] = useState("");
  const [detailRevision, setDetailRevision] = useState(0);
  const [competitorRows, setCompetitorRows] = useState([]);
  const [teamSections, setTeamSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardHydrating, setLeaderboardHydrating] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState("");
  const [scoreRefreshRevision, setScoreRefreshRevision] = useState(0);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState("");
  const [teamRefreshRevision, setTeamRefreshRevision] = useState(0);
  const [selectedTab, setSelectedTab] = useState("posted");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [awardView, setAwardView] = useState("bulletin");
  const [pdfSelection, setPdfSelection] = useState(null);
  const [myRegistration, setMyRegistration] = useState(null);
  const [personalLoading, setPersonalLoading] = useState(true);
  const [personalError, setPersonalError] = useState("");
  const [signedIn, setSignedIn] = useState(false);
  const [lastScoresChecked, setLastScoresChecked] = useState(null);
  const scoreSnapshotMatchId = useRef(null);
  const [detailPanel, setDetailPanel] = useState(null);
  const [expandedBreakdownKeys, setExpandedBreakdownKeys] = useState(() => new Set());

  useEffect(() => {
    setMatch(null); setSetupDetail(null); setRegistrationSummary(null); setTeamSections([]);
    setCompetitorRows([]); setLastScoresChecked(null); setExpandedBreakdownKeys(new Set());
    setMyRegistration(null); setSignedIn(false); setPersonalError(""); setPersonalLoading(true);
    scoreSnapshotMatchId.current = null;
  }, [matchId]);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");
      setSetupLoading(false);
      setTeamLoading(false);
      setTeamError("");
      setRegistrationSummaryError("");
      setSetupError("");

      try {
        const [matchResult, registrationResult] = await Promise.allSettled([
          getBullseyeMatch(matchId),
          getBullseyeMatchRegistrationSummary(matchId),
        ]);

        if (!active) {
          return;
        }

        if (matchResult.status !== "fulfilled") {
          throw matchResult.reason;
        }

        setMatch(matchResult.value);
        setRegistrationSummary(registrationResult.status === "fulfilled" ? registrationResult.value || null : null);
        if (registrationResult.status === "rejected") setRegistrationSummaryError("Registration availability could not load.");
        setLoading(false);

        setSetupLoading(true);
        getBullseyeMatchSetupDetail(matchId)
          .then((setupValue) => {
            if (active) {
              setSetupDetail(setupValue?.setupDetail || null);
            }
          })
          .catch(() => {
            if (active) {
              setSetupError("Course information could not load.");
            }
          })
          .finally(() => {
            if (active) {
              setSetupLoading(false);
            }
          });

      } catch (err) {
        if (!active) {
          return;
        }
        if ([401, 403, 404].includes(err.status)) setMatch(null);
        setError([403, 404].includes(err.status) ? "This match is unavailable. It may be private or no longer shared. Sign in with the owner account to manage a private match." : err.message || "Failed to load Bullseye match detail");
        setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [matchId, detailRevision]);

  useEffect(() => {
    let active = true;
    async function loadScores() {
      setLeaderboardLoading(true);
      setLeaderboardHydrating(false);
      setLeaderboardError("");
      try {
        const [pageResult, canonicalResult] = await Promise.allSettled([
          listBullseyeMatchLeaderboardsPage({ matchId, limit: BULLSEYE_DETAIL_INITIAL_ROW_LIMIT }),
          listCompetitionResults(matchId),
        ]);
        if (!active) return;
        if (pageResult.status === "rejected" && canonicalResult.status === "rejected") {
          throw pageResult.reason;
        }
        const page = pageResult.status === "fulfilled" ? pageResult.value : null;
        const pageRows = Array.isArray(page?.data) ? page.data : [];
        const canonicalRows = canonicalResult.status === "fulfilled"
          ? normalizeCanonicalBullseyeResultRows(canonicalResult.value) : [];
        let loadedRows = pageRows.length > 0 ? pageRows : canonicalRows;
        const hasPreviousSnapshot = scoreSnapshotMatchId.current === matchId;
        // Initial loads can stream pages; refreshes replace the snapshot only after every page succeeds.
        if (!hasPreviousSnapshot) setCompetitorRows(loadedRows);
        setLeaderboardLoading(false);
        if (pageResult.status === "rejected") {
          setLeaderboardError("Detailed standings could not load. Saved aggregate results may be incomplete.");
        }
        if (page?.nextToken) {
          setLeaderboardHydrating(true);
          await loadRemainingBullseyeLeaderboardPages(matchId, page.nextToken, (nextRows) => {
            loadedRows = mergeBullseyeRows(loadedRows, nextRows);
            if (active && !hasPreviousSnapshot) setCompetitorRows(loadedRows);
          });
        }
        if (active && pageResult.status === "fulfilled") {
          setCompetitorRows(loadedRows);
          scoreSnapshotMatchId.current = matchId;
          setLastScoresChecked(new Date().toISOString());
        }
      } catch (scoreError) {
        if (active) setLeaderboardError(scoreError?.message || "Failed to load Bullseye scores.");
      } finally {
        if (active) {
          setLeaderboardLoading(false);
          setLeaderboardHydrating(false);
        }
      }
    }
    loadScores();
    return () => { active = false; };
  }, [matchId, scoreRefreshRevision]);

  const hasScoreRows = competitorRows.length > 0;
  const isPostedResultsPage = location.pathname.endsWith("/results");
  const resultsPhase = useMemo(() => isPostedResultsPage || isResultsPhaseStatus(match?.status) || hasScoreRows, [isPostedResultsPage, match?.status, hasScoreRows]);
  const canExportResults = Boolean(match && (isInProgressDetail(match) || isCompletedDetail(match)));
  const shouldLoadTeams = resultsPhase && (isPostedResultsPage || selectedTab === "teams");
  useMatchPageRefresh(match?.matchId === matchId ? match : null,
    () => { setScoreRefreshRevision(value => value + 1); setDetailRevision(value => value + 1); },
    loading || leaderboardLoading || leaderboardHydrating || setupLoading, !isPostedResultsPage);

  useEffect(() => {
    if (!shouldLoadTeams) {
      setTeamLoading(false);
      return undefined;
    }

    let active = true;
    setTeamLoading(true);
    setTeamError("");

    listBullseyeTeamLeaderboards({ matchId })
      .then((teamValue) => {
        if (active) {
          setTeamSections(Array.isArray(teamValue) ? teamValue : []);
        }
      })
      .catch((teamLoadError) => {
        if (active) {
          setTeamError(teamLoadError?.message || "Failed to load team standings.");
          setTeamSections([]);
        }
      })
      .finally(() => {
        if (active) {
          setTeamLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [matchId, shouldLoadTeams, teamRefreshRevision]);

  useEffect(() => {
    setSelectedTab(resultsPhase ? "posted" : "setup");
  }, [resultsPhase]);

  useEffect(() => {
    let active = true;

    async function loadMyRegistrationState() {
      try {
        const user = await getCurrentUser().catch(() => null);
        if (!active) return;
        setSignedIn(Boolean(user));
        if (!user) { setMyRegistration(null); setPersonalError(""); return; }
        const registration = await getMyBullseyeMatchRegistration(matchId);
        if (active) {
          setMyRegistration(registration || null);
          setPersonalError("");
        }
      } catch {
        if (active) {
          setPersonalError("Your registration could not load.");
        }
      } finally {
        if (active) setPersonalLoading(false);
      }
    }

    loadMyRegistrationState();
    return () => {
      active = false;
    };
  }, [matchId, detailRevision]);

  const matchFacts = useMemo(() => setupFacts(setupDetail), [setupDetail]);
  const competitorCounts = useMemo(() => summaryCounts(competitorRows), [competitorRows]);
  const postedResultSections = useMemo(() => buildBullseyePostedSections(competitorRows, match?.bullseyeResultSettings), [competitorRows, match?.bullseyeResultSettings]);
  const pdfSection = pdfSelection?.matchId === matchId && postedResultSections.some(section => section.key === pdfSelection.key)
    ? pdfSelection.key : "all";
  const firstPrintedSection = pdfSection === "all" ? postedResultSections[0]?.key : pdfSection;
  const postedResultJumpOptions = useMemo(() => ([
    ...postedResultSections.map((section) => ({
      value: sectionAnchorId(section.key),
      label: `${bullseyeSectionNavLabel(section)}${bullseyeSectionNavMeta(section) ? ` - ${bullseyeSectionNavMeta(section)}` : ""}`,
    })),
    { value: "bullseye-competitor-cards", label: "Competitors - Score cards" },
    { value: "bullseye-team-results", label: "Teams - Team results" },
  ]), [postedResultSections]);
  const aggregateSection = postedResultSections[0] || null;
  const leaderboardPending = leaderboardLoading || leaderboardHydrating;
  const matchShapeLabel = useMemo(() => bullseyeMatchShapeLabel(match, matchFacts), [match, matchFacts]);
  const participationLabel = useMemo(() => bullseyeParticipationLabel(match), [match]);
  const postedSummaryLabel = aggregateSection
    ? participationCountsText(aggregateSection.competitorCount, aggregateSection.twentyTwoOnlyCount)
    : "Scores not posted yet";
  const registrationFacts = useMemo(
    () => buildBullseyeRegistrationFacts(registrationSummary),
    [registrationSummary],
  );
  const filteredCompetitorRows = useMemo(() => {
    const query = String(searchText || "").trim().toLowerCase();
    return rankBullseyeParticipationRows(competitorRows.map((row) => ({
      ...row,
      tieContext: bullseyeTieContext(row, "match::aggregate", "match", match?.bullseyeResultSettings),
      twentyTwoOnly: isBullseyeTwentyTwoOnlyRow(row),
      totalScore: numberOrZero(row.totalScore),
      xCount: numberOrZero(row.xCount),
    }))).filter((row) => {
      const matchesSearch = !query || String(row?.competitorName || "").toLowerCase().includes(query);
      const matchesStatus = statusFilter === "all" || String(row?.status || "").toLowerCase() === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [competitorRows, searchText, statusFilter, match?.bullseyeResultSettings]);
  const activeCompetitorFilterLabels = [
    statusFilter !== "all" ? formatStatus(statusFilter) : null,
    String(searchText || "").trim() ? `Search: ${String(searchText).trim()}` : null,
  ].filter(Boolean);

  const setupGroups = useMemo(
    () => (Array.isArray(setupDetail?.gunGroups) ? setupDetail.gunGroups : []),
    [setupDetail],
  );

  function handleCompetitorBreakdownToggle(row, index, isOpen) {
    const key = bullseyeRowKey(row, index);
    setExpandedBreakdownKeys((currentKeys) => {
      const nextKeys = new Set(currentKeys);
      if (isOpen) {
        nextKeys.add(key);
      } else {
        nextKeys.delete(key);
      }
      return nextKeys;
    });
  }

  const handlePostedResultJump = useCallback((event) => {
    const targetId = String(event.target.value || "").trim();
    if (!targetId) {
      return;
    }

    const target = document.getElementById(targetId);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `#${targetId}`);
    }
    event.target.value = "";
  }, []);

  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const handlePrintBulletin = useCallback(async () => {
    if (!canExportResults || leaderboardPending || leaderboardError || !postedResultSections.length) return;
    setPdfError("");
    setPdfBusy(true);
    try {
      const { downloadBullseyeBulletinPdf } = await import("../../lib/bullseyeBulletinPdf");
      await downloadBullseyeBulletinPdf(buildBullseyeBulletinDocument(match, postedResultSections, pdfSection));
    } catch (error) {
      setPdfError(error.message || "The bulletin could not be exported. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  }, [match, pdfSection, postedResultSections, canExportResults, leaderboardPending, leaderboardError]);

  const printedMatch = useRef(null);
  useEffect(() => {
    if (!isPostedResultsPage || !canExportResults || leaderboardPending || leaderboardError || !postedResultSections.length) return undefined;
    if (new URLSearchParams(location.search).get("print") === "1" && printedMatch.current !== matchId) {
      printedMatch.current = matchId;
      handlePrintBulletin();
    }
    const target = location.hash && document.getElementById(decodeURIComponent(location.hash.slice(1)));
    if (target) target.scrollIntoView?.({ block: "start" });
    return undefined;
  }, [isPostedResultsPage, canExportResults, leaderboardPending, leaderboardError, postedResultSections.length, location.search, location.hash, matchId, handlePrintBulletin]);

  if (match?.matchId === matchId && match.lifecycle?.cancelledAt) return <CancelledMatchDetail match={match} family="bullseye" registration={myRegistration} />;

  if (!isPostedResultsPage && match?.matchId === matchId && isCompletedDetail(match)) {
    return <CompletedMatchDetail key={matchId} match={match} family="bullseye"
      scoreRows={competitorRows} refreshRevision={scoreRefreshRevision}
      sections={completedBullseyeSections(competitorRows, postedResultSections)}
      summary={registrationSummary} summaryError={registrationSummaryError}
      registration={myRegistration} signedIn={signedIn} personalLoading={personalLoading} personalError={personalError}
      setup={setupDetail} setupLoading={setupLoading} setupError={setupError}
      scoresLoading={leaderboardPending} scoresError={leaderboardError}
      onRefresh={() => { setScoreRefreshRevision(value => value + 1); setDetailRevision(value => value + 1); }} />;
  }

  if (!isPostedResultsPage && match?.matchId === matchId && isInProgressDetail(match)) {
    return <InProgressMatchDetail key={matchId} match={match} family="bullseye"
      scoreRows={competitorRows} refreshRevision={scoreRefreshRevision}
      sections={bullseyeLiveSections(competitorRows, postedResultSections)} summary={registrationSummary} registration={myRegistration}
      signedIn={signedIn} personalLoading={personalLoading} personalError={personalError}
      setup={setupDetail} setupLoading={setupLoading} setupError={setupError}
      scoresLoading={leaderboardPending} scoresError={leaderboardError} lastChecked={lastScoresChecked}
      detailsLoading={loading} detailsError={error || registrationSummaryError}
      onRefresh={() => { setScoreRefreshRevision(value => value + 1); setDetailRevision(value => value + 1); }} />;
  }

  if (match?.matchId === matchId && !isPostedResultsPage && isUpcomingDetail(match, competitorRows.some(hasBullseyeScoreData))) {
    return <UpcomingMatchDetail key={matchId} match={match} family="bullseye" summary={registrationSummary} registration={myRegistration}
      registrationError={registrationSummaryError} setup={setupDetail} setupLoading={setupLoading} setupError={setupError}
      refreshError={error} onRetry={() => setDetailRevision((value) => value + 1)} />;
  }

  return (
    <section className={`leaderboard-page${isPostedResultsPage ? " bullseye-results-page portal-surface" : ""}`}>
      <Helmet>
        <title>{match?.matchName || "Bullseye Match"} | Range Pocket Competition</title>
      </Helmet>

      {loading && (
        <div className="leaderboard-card list-empty-panel status-loading-panel">
          <div className="owner-registration-section-label">Loading</div>
          <strong>Bullseye Match</strong>
        </div>
      )}
      {!loading && error && (
        <CompetitionErrorState
          eyebrow="Bullseye match"
          title="Match could not load"
          message={error}
          onRetry={() => window.location.reload()}
          links={[
            { to: `/auth/sign-in?returnTo=${encodeURIComponent(`/matches/bullseye/${matchId}`)}`, label: "Sign in" },
            { to: "/matches", label: "Matches" },
            { to: "/tournaments", label: "Tournaments" },
          ]}
          className="match-detail-error-panel"
        />
      )}

      {!loading && !error && match && (
        <>
          <RelatedMatchSelector match={match} family="bullseye" surface={isPostedResultsPage ? "results" : "detail"} />
          <div className={`leaderboard-detail-hero${hasMapLocation(match) ? "" : " leaderboard-detail-hero-no-map"}${isPostedResultsPage ? " bullseye-results-detail-hero" : ""}`}>
            <div className="leaderboard-detail-copy">
              <Link to={isPostedResultsPage ? `/matches/bullseye/${encodeURIComponent(matchId)}` : "/matches"} state={location.state} className="leaderboard-detail-back-link">
                <span aria-hidden="true">←</span>
                <span>{isPostedResultsPage ? "Back to match results" : "Matches"}</span>
              </Link>
              <h1>{match.matchName || "Bullseye Match"}</h1>
              <div className="public-match-actions" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                <PublicMatchDownloads match={match} family="bullseye" canExportResults={canExportResults}
                  bulletin={<BullseyeBulletinPrintControls sections={postedResultSections} selectedSection={pdfSection}
                    onSectionChange={key => setPdfSelection({ matchId, key })} onPrint={handlePrintBulletin}
                    busy={pdfBusy} error={pdfError}
                    disabled={leaderboardPending || Boolean(leaderboardError) || postedResultSections.length === 0} />}
                  scores={<BullseyeAwardsCsvExport key={matchId} matchName={match?.matchName || match?.name || match?.title}
                    rows={competitorRows} disabled={leaderboardPending || Boolean(leaderboardError) || postedResultSections.length === 0} />} />
                <MatchShareMenu match={match} family="bullseye" />
              </div>

              <div className="leaderboard-detail-chip-row">
                <span className="status-pill status-completed">{formatStatus(match.publicationState)}</span>
                <span className={`status-pill status-${isCompletedDetail(match) ? "completed" : "in_progress"}`}>{isCompletedDetail(match) ? "Match ended" : formatStatus(match.status)}</span>
                <span className="open-match-chip">{matchShapeLabel}</span>
                {participationLabel && <span className="open-match-chip">{participationLabel}</span>}
                {match.sanctionedType && (
                  <span className="open-match-chip">{String(match.sanctionedType).trim()}</span>
                )}
              </div>

              <div className="leaderboard-detail-facts">
                <div>
                  <span>Date</span>
                  <strong>{formatDate(match.matchDate || match.startDate)}</strong>
                </div>
                <div>
                  <span>Location</span>
                  <strong>{buildLocationLabel(match)}</strong>
                </div>
                <div>
                  <span>Program</span>
                  <strong>{setupLoading && !setupDetail ? "Loading" : matchShapeLabel}</strong>
                </div>
                <div>
                  <span>Competitors</span>
                  <strong aria-label="Confirmed competitor count">{Number.isInteger(registrationSummary?.activeParticipants) && registrationSummary.activeParticipants >= 0
                    ? registrationSummary.activeParticipants : "Unavailable"}</strong>
                </div>
                <div>
                  <span>Posted Scores</span>
                  <strong>{leaderboardPending ? "Loading" : leaderboardError ? "Unavailable" : competitorCounts.entered}</strong>
                </div>
                <div>
                  <span>Teams</span>
                  <strong>{matchFacts.teamCount > 0 ? matchFacts.teamCount : "None"}</strong>
                </div>
              </div>
              <div className="leaderboard-detail-actions">
                {resultsPhase && !isPostedResultsPage && (
                  <Link
                    to={`/matches/bullseye/${encodeURIComponent(matchId)}/results`}
                    className="match-search-button leaderboard-inline-link bullseye-primary-results-link"
                  >
                    View Posted Results
                  </Link>
                )}
                {resultsPhase && (
                  <Link
                    to={bullseyeDisplayPath(matchId)}
                    className="registration-auth-secondary leaderboard-inline-link"
                  >
                    Open Display
                  </Link>
                )}
                {!isPostedResultsPage && registrationFacts.registrationOpen && (
                  <Link
                    to={`/matches/bullseye/${encodeURIComponent(matchId)}/register`}
                    className="registration-auth-secondary leaderboard-inline-link"
                  >
                    {myRegistration ? "Manage Registration" : "Register"}
                  </Link>
                )}
                <button
                  type="button"
                  className="match-search-button"
                  onClick={() => setDetailPanel("match")}
                >
                  Match Info
                </button>
                <button
                  type="button"
                  className="match-search-button"
                  onClick={() => setDetailPanel("stage")}
                >
                  Stage Info
                </button>
              </div>
            </div>

            {hasMapLocation(match) && (
              <div className="leaderboard-detail-map-card">
                <MatchLocationMap
                  matches={[match]}
                  selectedMatchId={match.matchId}
                  mapLabel={buildLocationLabel(match)}
                  className="leaderboard-detail-map"
                />
                <div className="leaderboard-detail-map-meta">
                  <span>Address</span>
                  <strong>{buildAddressLabel(match)}</strong>
                </div>
              </div>
            )}
          </div>

          {resultsPhase && !isPostedResultsPage && (
            <section className="leaderboard-card bullseye-public-result-callout">
              <div className="bullseye-public-result-copy">
                <div className="owner-registration-section-label">Posted Results</div>
                <h2>{leaderboardError ? "Results temporarily unavailable" : leaderboardPending
                  ? "Loading posted results" : aggregateSection ? `${matchShapeLabel} results` : "Scores not posted yet"}</h2>
                <p aria-label="Posted competitor counts">{leaderboardError ? "Score totals are unavailable" : leaderboardPending
                  ? "Competitor totals are updating" : aggregateSection ? postedSummaryLabel : "No scores have been posted for this match."}</p>
              </div>
              <div className="bullseye-public-result-actions">
                <Link
                  to={`/matches/bullseye/${encodeURIComponent(matchId)}/results`}
                  className="match-search-button leaderboard-inline-link bullseye-primary-results-link"
                >
                  View Posted Results
                </Link>
                <Link
                  to={bullseyeDisplayPath(matchId)}
                  className="registration-auth-secondary leaderboard-inline-link"
                >
                  Fullscreen Display
                </Link>
              </div>
            </section>
          )}

          {resultsPhase && isPostedResultsPage && (
            <div className="match-controls leaderboard-card bullseye-leaderboard-controls">
              <div className="bullseye-leaderboard-controls-header">
                <div>
                  <div className="owner-registration-section-label">Leaderboard</div>
                  <h2>Posted Results</h2>
                  <p aria-label="Posted competitor counts">{leaderboardError ? "Result loading is incomplete" : leaderboardPending
                    ? "Competitor totals are updating" : postedSummaryLabel}</p>
                </div>
                <div className="leaderboard-display-action-group">
                  <Link
                    to={bullseyeDisplayPath(matchId)}
                    className="match-search-button booth-launch-button"
                  >
                    Open display
                  </Link>
                </div>
              </div>
              <div className="bullseye-results-section-jump">
                <label className="leaderboard-field" htmlFor="bullseye-results-section-jump">
                  <span>Result section</span>
                  <select
                    id="bullseye-results-section-jump"
                    className="match-sort"
                    defaultValue=""
                    onChange={handlePostedResultJump}
                  >
                    <option value="" disabled>Jump to section</option>
                    {postedResultJumpOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="leaderboard-field" htmlFor="bullseye-award-view">
                  <span>Award view</span>
                  <select
                    id="bullseye-award-view"
                    className="match-sort"
                    value={awardView}
                    onChange={(event) => setAwardView(event.target.value)}
                  >
                    {BULLSEYE_AWARD_VIEW_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              {(leaderboardLoading || leaderboardHydrating) && (
                <div className="leaderboard-meta">
                  {leaderboardLoading ? "Loading Bullseye scores." : "Loading remaining Bullseye scores in the background."}
                </div>
              )}
            </div>
          )}

          {leaderboardError && (
            <section className="leaderboard-card match-detail-empty-panel list-empty-panel" role="alert">
              <strong>{competitorRows.length > 0 ? "Some scores could not load" : "Scores could not load"}</strong>
              <p>{leaderboardError}</p>
              <button type="button" className="registration-auth-secondary leaderboard-inline-link"
                disabled={leaderboardPending} onClick={() => setScoreRefreshRevision((value) => value + 1)}>
                Retry scores
              </button>
            </section>
          )}

          {!resultsPhase && (
            <div className="leaderboard-card leaderboard-inline-action-panel public-match-registration-panel">
              <div className="leaderboard-card-row leaderboard-card-row--wrap">
                <h2>Registration</h2>
                <Link to={`/matches/bullseye/${encodeURIComponent(matchId)}/register`} className="match-search-button leaderboard-inline-link">
                  {myRegistration ? "Manage" : "Register"}
                </Link>
              </div>
              <div className="registration-readiness-strip public-match-readiness-strip">
                <div className={`registration-readiness-item ${registrationFacts.registered > 0 ? "is-ready" : "is-neutral"}`}>
                  <span>Registered</span>
                  <strong>{registrationFacts.registered}</strong>
                </div>
                <div className={`registration-readiness-item ${registrationFacts.confirmed > 0 ? "is-ready" : "is-neutral"}`}>
                  <span>Confirmed</span>
                  <strong>{registrationFacts.confirmed}</strong>
                </div>
                <div className={`registration-readiness-item ${registrationFacts.pending > 0 ? "is-missing" : "is-ready"}`}>
                  <span>Pending</span>
                  <strong>{registrationFacts.pending}</strong>
                </div>
                <div className={`registration-readiness-item ${registrationFacts.registrationOpen ? "is-ready" : "is-missing"}`}>
                  <span>Status</span>
                  <strong>{registrationFacts.registrationOpen ? "Open" : "Closed"}</strong>
                </div>
              </div>
            </div>
          )}

          {resultsPhase && isPostedResultsPage && (
            <div className={`bullseye-posted-results-board${leaderboardPending || leaderboardError ? " is-incomplete" : ""}`}>
              <BullseyeBulletinPrintHeader matchId={matchId}
                name={match?.matchName || match?.name || match?.title || "Bullseye Match"}
                date={formatDate(match?.matchDate || match?.startDate || match?.date)}
                location={buildLocationLabel(match) !== "Location not set" ? buildLocationLabel(match) : ""} />
              {match?.bullseyeResultSettings?.programNotes && (
                <section className="bullseye-grouping-program" aria-label="Published grouping policy">
                  <strong>Class Grouping · Published Program</strong>
                  <p>{match.bullseyeResultSettings.programNotes}</p>
                </section>
              )}
              {leaderboardLoading && postedResultSections.length === 0 && (
                <div className="leaderboard-card match-detail-empty-panel list-empty-panel">
                  <div className="owner-registration-section-label">Loading posted results</div>
                  <strong>Building Bullseye result sections</strong>
                </div>
              )}
              {!leaderboardPending && !leaderboardError && postedResultSections.length === 0 && (
                <div className="leaderboard-card match-detail-empty-panel list-empty-panel">
                  <div className="owner-registration-section-label">No posted results</div>
                  <strong>No scored Bullseye sections are ready to post yet</strong>
                  <div className="empty-filter-summary" aria-label="Posted result state">
                    <span>Empty shot slots are not treated as fired</span>
                    <span>Audited and official rows will appear here automatically</span>
                  </div>
                </div>
              )}
              {postedResultSections.map((section) => (
                <article
                  key={section.key}
                  id={sectionAnchorId(section.key)}
                  className={`leaderboard-card bullseye-posted-section bullseye-posted-section-${section.kind}${section.key === firstPrintedSection ? " is-first-print-section" : ""}`}
                  data-print-included={pdfSection === "all" || pdfSection === section.key}
                >
                  <div className="bullseye-posted-section-head">
                    <div>
                      <div className="owner-registration-section-label">{section.eyebrow}</div>
                      <h3>{section.title}</h3>
                      <p>{section.displayName}</p>
                    </div>
                    <div className="bullseye-posted-section-actions">
                      <Link
                        to={bullseyeSectionDisplayPath(matchId, section.key)}
                        className="registration-auth-secondary leaderboard-inline-link bullseye-section-display-link"
                      >
                        Display section
                      </Link>
                    </div>
                  </div>

                  <p className="bullseye-section-participation">{participationCountsText(section.competitorCount, section.twentyTwoOnlyCount)}</p>

                  <div className={`bullseye-award-view bullseye-award-view-${awardView}`}>
                    {awardView === "bulletin" && match?.bullseyeResultSettings?.classAwardThresholds && (leaderboardPending || leaderboardError)
                      ? <p className="bullseye-class-award-summary">Class awards need the complete entrant list. {leaderboardError ? "Retry scores to continue." : "Loading results..."}</p>
                      : buildBullseyeAwardBlocks(section, awardView, match?.bullseyeResultSettings).map((block) => (
                      <BullseyePostedAwardBlock key={block.key} block={block} sectionTitle={section.title} bulletin={awardView === "bulletin"} />
                    ))}
                  </div>
                </article>
              ))}
            </div>
          )}

          {resultsPhase && isPostedResultsPage && (
            <>
              <section id="bullseye-competitor-cards" className="bullseye-results-support-section">
                <div className="bullseye-results-support-head">
                  <div>
                    <div className="owner-registration-section-label">Competitors</div>
                    <h2>Competitor Score Cards</h2>
                  </div>
                  <form className="match-controls-row bullseye-leaderboard-filter-row" onSubmit={(event) => event.preventDefault()}>
                    <input
                      className="match-search"
                      type="text"
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                      placeholder="Search competitors"
                    />
                    <select className="match-sort" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                      <option value="all">All statuses</option>
                      <option value="unscored">Unscored</option>
                      <option value="in_progress">In Progress</option>
                      <option value="scored">Scored</option>
                      <option value="audited">Audited</option>
                      <option value="official">Recorded scores confirmed</option>
                      <option value="needs_refire">Needs Refire</option>
                    </select>
                    <div className="public-control-summary" aria-label="Visible competitors">
                      <strong>{leaderboardError && !competitorRows.length ? "-" : filteredCompetitorRows.length}</strong>
                      <span>{leaderboardError && !competitorRows.length ? "Unavailable" : leaderboardPending || leaderboardError ? "Loaded" : filteredCompetitorRows.length === 1 ? "competitor" : "competitors"}</span>
                    </div>
                  </form>
                </div>
              </section>
              <div className="leaderboard-grid bullseye-standings-grid">
                {leaderboardLoading && filteredCompetitorRows.length === 0 && (
                  <div className="leaderboard-card match-detail-empty-panel list-empty-panel">
                    <div className="owner-registration-section-label">Loading scores</div>
                    <strong>Loading competitor standings</strong>
                  </div>
                )}
                {!leaderboardPending && !leaderboardError && filteredCompetitorRows.length === 0 && (
                  <div className="leaderboard-card match-detail-empty-panel list-empty-panel">
                    <div className="owner-registration-section-label">No competitors</div>
                    <strong>No competitors match these filters</strong>
                    {activeCompetitorFilterLabels.length > 0 && (
                      <div className="empty-filter-summary" aria-label="Active filters">
                        {activeCompetitorFilterLabels.map((label) => (
                          <span key={label}>{label}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {filteredCompetitorRows.map((row, index) => {
                  const rowKey = bullseyeRowKey(row, index);
                  const breakdownOpen = expandedBreakdownKeys.has(rowKey);
                  return (
                  <React.Fragment key={rowKey}>
                  {filteredCompetitorRows[index - 1]?.participationGroup !== row.participationGroup && (
                    <h3 className="bullseye-score-card-group-heading">{row.participationGroup} Score Cards</h3>
                  )}
                  <div className="leaderboard-card match-card bullseye-standings-card">
                    <div className="leaderboard-card-row">
                      <strong>{row.rank != null && <>{row.isTied ? `T${row.rank}` : `#${row.rank}`} </>}{row.competitorName}</strong>
                      <span className={`status-pill status-${String(row.status || "").toLowerCase()}`}>{formatStatus(row.status)}</span>
                    </div>

                    <div className="bullseye-score-row">
                      <div>
                        <span>Score</span>
                        <strong>{bullseyeEnteredScoreText(row)}</strong>
                      </div>
                      <div>
                        <span>Class</span>
                        <strong><BullseyeClassificationLabel value={row.classification} /></strong>
                      </div>
                    </div>

                    <details className="bullseye-breakdown" onToggle={(event) => handleCompetitorBreakdownToggle(row, index, event.currentTarget.open)}>
                      <summary>Show Breakdown</summary>
                      {breakdownOpen && (
                        <div className="bullseye-breakdown-stack">
                          <div className="leaderboard-meta">{shotsText(row.shotsFired)}</div>
                          {sightDivisionLabel(row.division) && <div className="leaderboard-meta">{sightDivisionLabel(row.division)}</div>}
                          {Array.isArray(row.firearms) && row.firearms.map((firearm) => (
                            <div key={firearm.gunGroupId} className="bullseye-breakdown-card">
                              <div className="leaderboard-card-row">
                                <strong>{firearm.firearmTitle}</strong>
                                <span>{bullseyeEnteredScoreText(firearm)}</span>
                              </div>
                              <div className="leaderboard-meta">{shotsText(firearm.shotsFired)}</div>
                              {Array.isArray(firearm.courses) && firearm.courses.map((course) => (
                                <div key={`${firearm.gunGroupId}-${course.courseName}`} className="bullseye-breakdown-course">
                                  <div className="leaderboard-card-row">
                                    <span>{course.courseName}</span>
                                    <span>{bullseyeEnteredScoreText(course)}</span>
                                  </div>
                                  <div className="leaderboard-meta">{shotsText(course.shotsFired)}</div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </details>
                  </div>
                  </React.Fragment>
                );
                })}
              </div>
            </>
          )}

          {resultsPhase && isPostedResultsPage && (
            <div id="bullseye-team-results" className="bullseye-team-sections bullseye-results-support-section">
              <div className="bullseye-results-support-head">
                <div>
                  <div className="owner-registration-section-label">Teams</div>
                  <h2>Team Results</h2>
                </div>
                <button
                  type="button"
                  className="registration-auth-secondary"
                  onClick={() => setTeamRefreshRevision((revision) => revision + 1)}
                  disabled={teamLoading}
                >
                  {teamLoading ? "Refreshing teams..." : teamError ? "Retry teams" : "Refresh teams"}
                </button>
              </div>
              {teamLoading && (
                <div className="leaderboard-card match-detail-empty-panel list-empty-panel">
                  <div className="owner-registration-section-label">Loading teams</div>
                  <strong>Loading team standings</strong>
                </div>
              )}
              {!teamLoading && teamError && (
                <div className="leaderboard-card match-detail-empty-panel list-empty-panel" role="alert">
                  <div className="owner-registration-section-label">Teams unavailable</div>
                  <strong>{teamError}</strong>
                </div>
              )}
              {!teamLoading && !teamError && teamSections.length === 0 && (
                <div className="leaderboard-card match-detail-empty-panel list-empty-panel">
                  <div className="owner-registration-section-label">No teams</div>
                  <strong>No team entries yet</strong>
                  <div className="empty-filter-summary" aria-label="Team standings state">
                    <span>Team rows: 0</span>
                  </div>
                </div>
              )}
              {teamSections.map((section) => (
                <div key={section.configurationId} className="leaderboard-card bullseye-team-section-card">
                  <div className="leaderboard-card-row">
                    <strong>{section.configurationName}</strong>
                    <span>{section.teams.length} {section.teams.length === 1 ? "team" : "teams"}</span>
                  </div>
                  <div className="bullseye-team-stack">
                    {section.teams.map((team) => (
                      <div key={team.teamId} className="bullseye-team-entry-card">
                        <div className="leaderboard-card-row">
                          <strong>{team.rank != null ? `#${team.rank} ` : ""}{team.teamName}</strong>
                          <span>{scoreText(team.totalScore, team.xCount)}</span>
                        </div>
                        <div className="bullseye-team-membership">
                          <span>
                            {team.memberCount ?? team.members.length}
                            {Number(section.teamSize) > 0 ? `/${section.teamSize}` : ""} eligible members
                          </span>
                          {(team.isRosterComplete === false || Number(section.teamSize) > (team.memberCount ?? team.members.length)) && (
                            <strong>Incomplete team · Not ranked</strong>
                          )}
                        </div>
                        <div className="bullseye-member-stack">
                          {team.members.map((member) => (
                            <div key={member.competitorId} className="leaderboard-meta">
                              {member.competitorName} · {scoreText(member.totalScore, member.xCount)}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

        </>
      )}

      {detailPanel === "match" && (
        <DetailOverlay title="Match Info" onClose={() => setDetailPanel(null)}>
          <div className="setup-summary-grid">
            <div className="setup-summary-card">
              <div className="setup-summary-card-head">
                <strong>Discipline</strong>
                <span>{formatSetupLabel(match?.selectedPistolDiscipline || "Bullseye")}</span>
              </div>
            </div>
            <div className="setup-summary-card">
              <div className="setup-summary-card-head">
                <strong>Sights</strong>
                <span>{formatSetupLabel(match?.selectedSightMode || "-")}</span>
              </div>
            </div>
            <div className="setup-summary-card">
              <div className="setup-summary-card-head">
                <strong>Firearms</strong>
                <span>{matchFacts.firearmCount}</span>
              </div>
            </div>
            <div className="setup-summary-card">
              <div className="setup-summary-card-head">
                <strong>Stages</strong>
                <span>{matchFacts.totalStages}</span>
              </div>
            </div>
          </div>
        </DetailOverlay>
      )}

      {detailPanel === "stage" && (
        <DetailOverlay title="Stage Info" onClose={() => setDetailPanel(null)}>
          {setupLoading && setupGroups.length === 0 ? (
            <div className="leaderboard-card match-detail-empty-panel list-empty-panel">
              <div className="owner-registration-section-label">Loading setup</div>
              <strong>Loading stage information</strong>
            </div>
          ) : (
            <div className="setup-detail-grid">
              {setupGroups.map((group) => (
              <div key={group.gunGroupId} className="setup-detail-card setup-detail-card--group">
                <div className="setup-detail-card-head">
                  <strong>{group.title || "Firearm"}</strong>
                  <span>{Array.isArray(group.stages) ? group.stages.length : 0} stages</span>
                </div>
                <div className="setup-detail-stack">
                  {(Array.isArray(group.stages) ? group.stages : []).map((stage, index) => {
                    const stageStrings = Array.isArray(stage?.strings) ? stage.strings : [];
                    const scoreBlocks = new Set(
                      stageStrings.map((entry) => entry?.scoringBlockIndex ?? entry?.seriesGroupIndex ?? "0"),
                    ).size;
                    const meta = [
                      Number.isFinite(Number(stage?.distanceYds)) ? `${stage.distanceYds} yd` : "",
                      BULLSEYE_FIRE_TYPE_LABELS[stage?.fireTypeRaw] || formatSetupLabel(stage?.fireType),
                      scoreBlocks > 1 ? `${scoreBlocks} score blocks` : "1 score block",
                    ].filter(Boolean);
                    return (
                      <div key={stage.stageId || `${group.gunGroupId}-stage-${index}`} className="setup-detail-subcard">
                        <div className="setup-detail-card-head">
                          <strong>{stage.courseGroupName || stage.name || `Stage ${index + 1}`}</strong>
                          <span>{stageStrings.length} series</span>
                        </div>
                        {meta.length > 0 && <div className="setup-detail-card-meta">{meta.join(" • ")}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
              ))}
            </div>
          )}
        </DetailOverlay>
      )}
    </section>
  );
}

function BullseyeLeaderboardMatchDisplay() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [match, setMatch] = useState(null);
  const [leaderboardRows, setLeaderboardRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [matchError, setMatchError] = useState("");
  const [rowsError, setRowsError] = useState("");
  const [rowsRevision, setRowsRevision] = useState(0);
  const error = matchError || rowsError;
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [topCount, setTopCount] = useState(() => normalizeDisplayRowCount(getDisplayRowsParam(searchParams), BULLSEYE_DISPLAY_DEFAULT_ROW_LIMIT));
  const [displaySectionKey, setDisplaySectionKey] = useState(() => String(searchParams.get("section") || ""));
  const displaySections = useMemo(() => buildBullseyePostedSections(leaderboardRows, match?.bullseyeResultSettings), [leaderboardRows, match?.bullseyeResultSettings]);
  const rows = useMemo(() => getBullseyePostedSectionRows(leaderboardRows, displaySectionKey, match?.bullseyeResultSettings), [leaderboardRows, displaySectionKey, match?.bullseyeResultSettings]);
  const manualSectionSelected = searchParams.has("section");

  useEffect(() => {
    setTopCount(normalizeDisplayRowCount(getDisplayRowsParam(searchParams), BULLSEYE_DISPLAY_DEFAULT_ROW_LIMIT));
    setDisplaySectionKey(String(searchParams.get("section") || ""));
  }, [searchParams]);

  useEffect(() => {
    let active = true;

    async function loadDisplayMatch() {
      setLoading(true);
      setMatchError("");

      try {
        const matchValue = await getBullseyeMatch(matchId);
        if (!active) return;
        setMatch(matchValue || null);
      } catch (loadError) {
        if (!active) return;
        setMatchError(loadError?.message || "Failed to load Bullseye display leaderboard.");
        setMatch(null);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadDisplayMatch();
    return () => {
      active = false;
    };
  }, [matchId]);

  useEffect(() => {
    let active = true;
    let pending = false;

    setLeaderboardLoading(true);
    setLeaderboardRows([]);
    setLastUpdatedAt(null);
    setRowsError("");
    async function loadDisplayRows() {
      if (pending) return;
      pending = true;
      try {
        const leaderboardValue = await listBullseyeMatchLeaderboards({ matchId });
        if (!active) return;
        setLeaderboardRows(leaderboardValue);
        setLastUpdatedAt(new Date());
        setRowsError("");
      } catch (loadError) {
        if (active) setRowsError(loadError?.message || "Failed to load Bullseye display leaderboard.");
      } finally {
        pending = false;
        if (active) setLeaderboardLoading(false);
      }
    }
    loadDisplayRows();

    const refreshTimer = window.setInterval(() => {
      if (active) loadDisplayRows();
    }, BULLSEYE_DISPLAY_REFRESH_MS);

    return () => {
      active = false;
      window.clearInterval(refreshTimer);
    };
  }, [matchId, rowsRevision]);

  const handleReachedEnd = useBullseyeDisplayRotation({
    matchId,
    sections: displaySections,
    sectionKey: displaySectionKey,
    manual: manualSectionSelected,
    intervalMs: BULLSEYE_DISPLAY_SECTION_ROTATE_MS,
    setSectionKey: setDisplaySectionKey,
  });

  function handleTopCountChange(nextValue) {
    const normalizedValue = normalizeDisplayRowCount(nextValue, BULLSEYE_DISPLAY_DEFAULT_ROW_LIMIT);
    setTopCount(normalizedValue);
    const nextParams = new URLSearchParams(searchParams);
    if (normalizedValue === BULLSEYE_DISPLAY_DEFAULT_ROW_LIMIT) {
      nextParams.delete("rows");
    } else {
      nextParams.set("rows", String(normalizedValue));
    }
    nextParams.delete("top");
    setSearchParams(nextParams, { replace: true, state: location.state });
  }

  function handleSectionChange(nextValue) {
    const nextSectionKey = String(nextValue || "");
    setDisplaySectionKey(nextSectionKey);
    const nextParams = new URLSearchParams(searchParams);
    if (nextSectionKey) {
      nextParams.set("section", nextSectionKey);
      nextParams.set("rows", "all");
    } else {
      nextParams.delete("section");
      if (topCount === "all") {
        nextParams.delete("rows");
      }
    }
    nextParams.delete("top");
    setSearchParams(nextParams, { replace: true, state: location.state });
  }

  return (
    <section className="leaderboard-page leaderboard-page-booth">
      <Helmet>
        <title>{match?.matchName ? `${match.matchName} Bullseye Leaderboard | Range Pocket Competition` : "Bullseye Leaderboard | Range Pocket Competition"}</title>
      </Helmet>

      {loading && (
        <BoothDisplayStatus
          eyebrow="Bullseye leaderboard"
          title="Loading leaderboard"
          message="Preparing the fullscreen display"
          bannerSrc={BULLSEYE_LEADERBOARD_BANNER_SRC}
          bannerAlt="Bullseye leaderboard powered by Range Pocket"
        />
      )}
      {!loading && error && (
        <BoothDisplayStatus
          eyebrow="Bullseye leaderboard"
          title="Leaderboard unavailable"
          message={error}
          bannerSrc={BULLSEYE_LEADERBOARD_BANNER_SRC}
          bannerAlt="Bullseye leaderboard powered by Range Pocket"
          actions={(
            <>
              <button type="button" className="match-search-button" onClick={() => matchError ? window.location.reload() : setRowsRevision((value) => value + 1)}>
                Try Again
              </button>
              <Link to={`/matches/bullseye/${encodeURIComponent(matchId)}`} className="registration-auth-secondary leaderboard-inline-link">
                Back to Match
              </Link>
            </>
          )}
        />
      )}
      {!loading && !error && (
        <BoothLeaderboardDisplay
          match={match}
          rows={rows}
          topCount={topCount}
          onTopCountChange={handleTopCountChange}
          onExit={() => navigate(displayReturn(location, `/matches/bullseye/${encodeURIComponent(matchId)}`))}
          leaderScore={null}
          bannerSrc={BULLSEYE_LEADERBOARD_BANNER_SRC}
          bannerAlt="Bullseye leaderboard powered by Range Pocket"
          rowClassName="booth-bullseye-row"
          displayClassName="booth-leaderboard-bullseye"
          teamColumnLabel="X"
          scoreColumnLabel="Score"
          performanceColumnLabel="Shots"
          controlLabel="Rows per group"
          rowLimitPerGroup
          autoHideControls
          scrollResetKey={`${displaySectionKey}|${manualSectionSelected}`}
          onReachedEnd={handleReachedEnd}
          displaySectionLabel={displaySections.find((section) => section.key === displaySectionKey)?.title || "Grand Aggregate"}
          lastUpdatedLabel={lastUpdatedAt ? `Updated ${formatLastUpdated(lastUpdatedAt)}` : ""}
          filterControls={(
            <><RelatedMatchSelector match={match} family="bullseye" surface="display" />
            <label className="booth-top-control booth-filter-control">
              <span>Section</span>
              <select
                value={displaySectionKey}
                onChange={(event) => handleSectionChange(event.target.value)}
              >
                <option value="">Overall</option>
                {displaySections.map((section) => (
                  <option key={section.key} value={section.key}>
                    {section.title}
                  </option>
                ))}
              </select>
            </label></>
          )}
          emptyMessage={leaderboardLoading ? "Loading Bullseye scores" : "No Bullseye scores yet"}
        />
      )}
    </section>
  );
}

export const BullseyeLeaderboardMatchDetail_Page = {
  path: "/matches/bullseye/:matchId",
  element: <BullseyeLeaderboardMatchDetail />,
};

export const BullseyePostedResults_Page = {
  path: "/matches/bullseye/:matchId/results",
  element: <BullseyeLeaderboardMatchDetail />,
};

export const BullseyeLeaderboardMatchDisplay_Page = {
  path: "/matches/bullseye/:matchId/display",
  element: <BullseyeLeaderboardMatchDisplay />,
};
